/**
 * Spraywall — the pure logic of the Spraywall add-on.
 *
 * One copy for every runtime that needs it: the client (problem editor,
 * hold-marking review, smart marking, the log block), the `api` and `vision`
 * Cloud Functions (validation on every write, hold matching after a photo
 * replacement, the smart-marking candidate choice) and `email` (the recap
 * block). Pure: no Firebase, no React, no Node builtin, no `window`, no clock
 * and no `Math.random` — the one random part (the RANSAC sampling) runs on a
 * seeded generator, so the same input always gives the same transform.
 *
 * Coordinates: a hold's `x`, `y` and `poly` are normalized to its photo
 * (0–1, origin top-left); `poly` is the compact string "x1,y1;x2,y2;…" with
 * 4 decimals and at most 16 points. A "point array" is `[[x, y], …]` in ONE
 * coordinate space. The matching functions work in PIXELS, because two photos
 * of one wall may differ in size and aspect ratio (a different crop): old
 * holds are denormalized with the old photo size, proposals with the new one.
 */
import { GRADE_SCALE } from "./gradeScale.js";

/** The ceiling of holds on one wall version (the top hold band of the market; ~420 KB of doc). */
export const MAX_SPRAY_HOLDS = 1500;

/** The accepted photo-to-photo transform: the alignment sliders and the server check share these bounds. */
export const MIN_TRANSFORM_SCALE = 0.5;
export const MAX_TRANSFORM_SCALE = 2;
export const MAX_TRANSFORM_ROT = Math.PI / 4;

/** The closed style-tag list — closed because the problem list filters on it. */
export const SPRAY_TAGS = Object.freeze(["crimpy", "slopey", "pinchy", "pockety", "juggy", "compression", "dynamic", "technical", "powerful", "endurance"]);

/** The closed report reasons of a problem report. */
export const SPRAY_REPORT_REASONS = Object.freeze(["wrongProblem", "offensive", "other"]);

// ── Matching constants (module-private; the real-photo fixture re-tunes them) ──

// A hold moves at most this fraction of the image diagonal between two photos; beyond it the manual sliders take over.
const MAX_SHIFT_FRAC = 0.15;
// Centroid agreement under a transform: detection masks of one hold differ by a few pixels between two photos.
const INLIER_PX = 10;
// Upper bound on the 2-pair hypotheses one estimate may try, so a pathological input cannot run unbounded.
const RANSAC_ITERS = 20000;
// Below this many agreeing holds a transform is a coincidence, not a consensus.
const MIN_INLIERS = 8;
// Two outlines of one hold overlap at least this much after alignment; neighbouring holds rarely reach it.
const MATCH_IOU = 0.4;
// A consensus must also cover this share of the old holds: in a dense wall any transform finds random agreements.
const MIN_INLIER_FRAC = 0.25;
// Candidate partners kept per old hold — bounds the pair list on a 1500 × 2000 wall.
const PAIRS_PER_HOLD = 160;
// Old holds whose candidates seed hypotheses; several, so one hold missing from the new photo does not sink the estimate.
const ANCHOR_COUNT = 12;
// Neighbours of an anchor that check a hypothesis locally before the costly global scoring.
const LOCAL_NEIGHBORS = 8;
// How many of those neighbours must agree — random agreement on 4 of 8 is rare even on a dense wall.
const LOCAL_MIN = 4;
// The shortest baseline of a 2-pair sample: a shorter one makes the rotation estimate noise-dominated.
const MIN_BASELINE_PX = 4 * INLIER_PX;
// Independent anchors that must grow the same model before the search stops early.
const CONFIRMATIONS = 3;
// A growing hypothesis is dropped once its inlier share is under half the leader's (over at least this many holds).
const LOSING_SHARE = 0.5;
const LOSING_MIN_POINTS = 20;
// The fixed seed of the sampling generator: a run is reproducible, so a test result is too.
const RANSAC_SEED = 0x5eed5;
// Scanlines of the polygon intersection: exact in x, midpoint rule in y — well inside ±0.02 IoU.
const OVERLAP_SCANLINES = 64;

// ── Problem holds ──

const PROBLEM_ROLES = new Set(["start", "hand", "foot", "finish"]);
const MIN_PROBLEM_HOLDS = 2;
const MAX_PROBLEM_HOLDS = 60;

/** The version's holds by id; a non-array (or malformed entries) contributes nothing. */
function sprayHoldIndex(versionHolds) {
  const byId = new Map();
  if (!Array.isArray(versionHolds)) return byId;
  for (const h of versionHolds) {
    if (h && typeof h.id === "string") byId.set(h.id, h);
  }
  return byId;
}

function isProblemEntry(e) {
  return !!e && typeof e === "object" && typeof e.h === "string" && PROBLEM_ROLES.has(e.role);
}

function uniqueList(ids) {
  return [...new Set(ids)];
}

/**
 * The validator of a problem's hold list. `null` when valid, else
 * `{ code, holdIds? }` — the FIRST failing check wins, in a fixed order, so
 * the editor and the callable show the same message for the same list.
 * A malformed entry is `unknownHold`: the code list stays closed (each code
 * has a `spraywall.error.<code>` i18n key).
 */
export function validateProblemHolds(holds, versionHolds, footRule) {
  if (!Array.isArray(holds) || holds.length < MIN_PROBLEM_HOLDS || holds.length > MAX_PROBLEM_HOLDS) {
    return { code: "holdCount" };
  }
  const byId = sprayHoldIndex(versionHolds);

  const unknown = [];
  let malformed = false;
  for (const e of holds) {
    if (!isProblemEntry(e)) {
      malformed = true;
      if (e && typeof e.h === "string") unknown.push(e.h);
    } else if (!byId.has(e.h)) {
      unknown.push(e.h);
    }
  }
  if (malformed || unknown.length > 0) return { code: "unknownHold", holdIds: uniqueList(unknown) };

  const seen = new Set();
  const dup = [];
  for (const e of holds) {
    if (seen.has(e.h)) dup.push(e.h);
    seen.add(e.h);
  }
  if (dup.length > 0) return { code: "duplicateHold", holdIds: uniqueList(dup) };

  const missing = holds.filter((e) => byId.get(e.h).missing === true).map((e) => e.h);
  if (missing.length > 0) return { code: "missingHold", holdIds: missing };

  const feetOnly = holds.filter((e) => byId.get(e.h).feetOnly === true && e.role !== "foot").map((e) => e.h);
  if (feetOnly.length > 0) return { code: "feetOnlyRole", holdIds: feetOnly };

  if (footRule === "noFeet" && holds.some((e) => e.role === "foot")) return { code: "noFeetRule" };

  const starts = holds.filter((e) => e.role === "start").length;
  if (starts < 1 || starts > 2) return { code: "startCount" };
  const finishes = holds.filter((e) => e.role === "finish").length;
  if (finishes < 1 || finishes > 2) return { code: "finishCount" };
  return null;
}

/**
 * The hold ids a problem needs fixed: holds that went `missing` on its
 * version, and feet-only holds it uses in a non-`foot` role. Unique, in
 * problem order; ids unknown to the version are ignored.
 */
export function fixableHoldIds(problemHolds, versionHolds) {
  if (!Array.isArray(problemHolds)) return [];
  return uniqueList(fixableEntries(problemHolds, sprayHoldIndex(versionHolds)).map((e) => e.h));
}

/**
 * The problem entries behind `fixableHoldIds`, against a prebuilt index —
 * `needsReviewDelta` checks every problem of a version twice (before and
 * after), so it builds the two indexes once instead of once per problem.
 */
function fixableEntries(problemHolds, byId) {
  const out = [];
  if (!Array.isArray(problemHolds)) return out;
  for (const e of problemHolds) {
    if (!isProblemEntry(e)) continue;
    const vh = byId.get(e.h);
    if (!vh) continue;
    if (vh.missing === true || (vh.feetOnly === true && e.role !== "foot")) out.push(e);
  }
  return out;
}

/**
 * Which problems a hold step sends to review, and which it gives back.
 * `problems` = `[{ id, status, holds }]` (every problem of the version),
 * `holdsBefore` / `holdsAfter` = the version's holds around the step.
 *
 * - `newlyAffected` — `[{ id, role }]`, any status: problems whose fixable
 *   holds after the step include one they did not have before. Comparing to
 *   "before" is the point: a wall that already carries a `missing` hold must
 *   not list its problems again on every unrelated edit (the dry-run dialog
 *   would otherwise ask about the same routes forever). `role` is the
 *   problem's first role at such a hold, for the dialog row.
 * - `toFlip` — ids of `active` problems with anything to fix after the step.
 * - `toActivate` — ids of `needsReview` problems with nothing left to fix
 *   (a restore, a feet-only flag switched off): `needsReview` is always
 *   derived from the fixable holds, so the status follows them back.
 * A `hidden` problem is never flipped either way. Together the two lists
 * keep the invariant "an active problem never points at a missing hold".
 */
export function needsReviewDelta(problems, holdsBefore, holdsAfter) {
  const newlyAffected = [];
  const toFlip = [];
  const toActivate = [];
  if (!Array.isArray(problems)) return { newlyAffected, toFlip, toActivate };
  const beforeIndex = sprayHoldIndex(holdsBefore);
  const afterIndex = sprayHoldIndex(holdsAfter);
  for (const p of problems) {
    if (!p || typeof p !== "object" || typeof p.id !== "string" || p.id === "") continue;
    const after = fixableEntries(p.holds, afterIndex);
    const before = new Set(fixableEntries(p.holds, beforeIndex).map((e) => e.h));
    const fresh = after.find((e) => !before.has(e.h));
    if (fresh) newlyAffected.push({ id: p.id, role: fresh.role });
    if (p.status === "active" && after.length > 0) toFlip.push(p.id);
    else if (p.status === "needsReview" && after.length === 0) toActivate.push(p.id);
  }
  return { newlyAffected, toFlip, toActivate };
}

/**
 * Every hold id used by any of the given problem documents — any status, any
 * visibility, so a hidden or private problem that comes back later never
 * points at a deleted id. A used hold that leaves the wall becomes
 * `missing: true`; an unused one is dropped. Malformed input is skipped.
 */
export function usedHoldIds(problems) {
  const out = new Set();
  if (!Array.isArray(problems)) return out;
  for (const p of problems) {
    if (!p || typeof p !== "object" || !Array.isArray(p.holds)) continue;
    for (const e of p.holds) {
      if (e && typeof e === "object" && typeof e.h === "string" && e.h !== "") out.add(e.h);
    }
  }
  return out;
}

/**
 * The `missing` holds a problem write may drop from the version: the
 * candidates whose version hold is `missing: true` and that none of
 * `otherProblems` uses (`usedHoldIds`, so any status and visibility). A
 * missing hold only keeps an id alive for the problems pointing at it; once
 * the last one lets go, nothing else can remove it (`applyHoldEdits` refuses
 * a missing target). With `otherProblems = []` it answers "is any candidate
 * missing at all", so the caller can skip reading the version's problems.
 * Unique ids in candidate order; malformed input is skipped.
 */
export function releasableMissingHoldIds(candidateIds, versionHolds, otherProblems) {
  if (!Array.isArray(candidateIds) || !Array.isArray(versionHolds)) return [];
  const missing = new Set();
  for (const h of versionHolds) {
    if (h && typeof h === "object" && typeof h.id === "string" && h.missing === true) missing.add(h.id);
  }
  const used = usedHoldIds(otherProblems);
  const out = [];
  for (const id of candidateIds) {
    if (typeof id === "string" && missing.has(id) && !used.has(id) && !out.includes(id)) out.push(id);
  }
  return out;
}

/**
 * May an author WITHOUT `spraywall.moderate` save this hold list on a problem
 * that already has a send? Set rule (holds are a set of `{h, role}`):
 * F = the old entries whose hold is fixable, K = the other old entries.
 * (a) every K entry survives unchanged; (b) the rest of the new list, R, is
 * no longer than F; (c) no R hold is missing or already used in K;
 * (d) the R roles are free — their limits (start / finish counts, a
 * feet-only hold only as `foot`, which also covers (e): a fixable feet-only
 * hold may come back as `foot`; unknown or repeated holds) belong to
 * `validateProblemHolds`, which the caller runs too, so the author sees the
 * precise error code rather than a generic "locked".
 * Knows nothing of capabilities: the caller skips it for a moderator.
 */
export function lockedHoldsEditAllowed(oldHolds, newHolds, versionHolds) {
  if (!Array.isArray(newHolds)) return false;
  const oldList = Array.isArray(oldHolds) ? oldHolds.filter(isProblemEntry) : [];
  const byId = sprayHoldIndex(versionHolds);
  const fixable = new Set(fixableHoldIds(oldList, versionHolds));
  const kept = oldList.filter((e) => !fixable.has(e.h));
  const fCount = oldList.length - kept.length;

  const keyOf = (e) => `${e.h}\u0000${e.role}`;
  const keptKeys = new Set(kept.map(keyOf));
  const keptHolds = new Set(kept.map((e) => e.h));
  const matched = new Set();
  const rest = [];
  for (const e of newHolds) {
    if (!isProblemEntry(e)) return false;
    const k = keyOf(e);
    if (keptKeys.has(k) && !matched.has(k)) matched.add(k);
    else rest.push(e);
  }
  if (matched.size !== keptKeys.size) return false;
  if (rest.length > fCount) return false;

  return rest.every((e) => byId.get(e.h)?.missing !== true && !keptHolds.has(e.h));
}

/**
 * The consensus grade of a problem: the LOWER median of the vote histogram
 * `{ [gradeIdx]: count }` — with an even count the easier grade wins, so one
 * sandbagger cannot lift a problem. `null` without a valid vote.
 */
export function consensusGrade(gradeVotes) {
  if (!gradeVotes || typeof gradeVotes !== "object") return null;
  const buckets = [];
  let total = 0;
  for (const [key, count] of Object.entries(gradeVotes)) {
    if (!/^(0|[1-9]\d*)$/.test(key)) continue;
    const idx = Number(key);
    if (idx >= GRADE_SCALE.length) continue;
    if (!Number.isInteger(count) || count <= 0) continue;
    buckets.push([idx, count]);
    total += count;
  }
  if (total === 0) return null;
  buckets.sort((a, b) => a[0] - b[0]);
  const target = Math.floor((total - 1) / 2);
  let seen = 0;
  for (const [idx, count] of buckets) {
    seen += count;
    if (seen > target) return idx;
  }
  return null;
}

/**
 * May a plain member create problems on this wall? The wall's own switch
 * wins when set, then the gym default (`settings.sprayWall`), else no.
 */
export function effectiveMembersCanCreate(wall, gymSettings) {
  if (typeof wall?.membersCanCreate === "boolean") return wall.membersCanCreate;
  const gymDefault = gymSettings?.sprayWall?.membersCanCreate;
  if (typeof gymDefault === "boolean") return gymDefault;
  return false;
}

const HOLD_NOTE_MAX = 40;

/**
 * The hold note: trimmed; blank means "delete" (`null`). Counted in code
 * points, so an accented or emoji note is not cut short by UTF-16 units.
 */
export function normalizeHoldNote(text) {
  if (text === null || text === undefined) return null;
  if (typeof text !== "string") throw new Error("invalidNote");
  const trimmed = text.trim();
  if (trimmed === "") return null;
  if ([...trimmed].length > HOLD_NOTE_MAX) throw new Error("noteTooLong");
  return trimmed;
}

// ── Polygon format ──

/** The most points a stored hold outline may have (and the hold editor lets a manager draw). */
export const POLY_MAX_POINTS = 16;
const POLY_NUM = "(?:0(?:\\.\\d{1,4})?|1(?:\\.0{1,4})?)";
const POLY_PAIR = `${POLY_NUM},${POLY_NUM}`;
// The stored format, checked textually: 3–16 points, each coordinate in [0, 1] with at most 4 decimals.
const HOLD_POLY_RE = new RegExp(`^${POLY_PAIR}(?:;${POLY_PAIR}){2,${POLY_MAX_POINTS - 1}}$`);
const DECODE_NUM_RE = /^-?\d+(?:\.\d+)?$/;

function isPoint(p) {
  return Array.isArray(p) && p.length >= 2 && Number.isFinite(p[0]) && Number.isFinite(p[1]);
}

function isPointList(points, min) {
  return Array.isArray(points) && points.length >= min && points.every(isPoint);
}

function round4(v) {
  const r = Number(v.toFixed(4));
  return r === 0 ? 0 : r; // no "-0" in the stored text
}

/** Normalized point array → `"x1,y1;x2,y2;…"`, 4 decimals. `null` for a non-array or a non-finite point. */
export function encodePoly(points) {
  if (!isPointList(points, 1)) return null;
  return points.map((p) => `${round4(p[0])},${round4(p[1])}`).join(";");
}

/** The compact string → point array; `null` on anything malformed. Range and count are `validateHoldPoly`'s job. */
export function decodePoly(str) {
  if (typeof str !== "string" || str === "") return null;
  const out = [];
  for (const part of str.split(";")) {
    const xy = part.split(",");
    if (xy.length !== 2 || !DECODE_NUM_RE.test(xy[0]) || !DECODE_NUM_RE.test(xy[1])) return null;
    out.push([Number(xy[0]), Number(xy[1])]);
  }
  return out;
}

function triangleArea(a, b, c) {
  return Math.abs((b[0] - a[0]) * (c[1] - a[1]) - (c[0] - a[0]) * (b[1] - a[1])) / 2;
}

/**
 * Visvalingam–Whyatt on the closed outline: drop the vertex spanning the
 * smallest triangle with its neighbours until `maxPoints` remain. Ties go to
 * the lowest index, so the result is deterministic. Non-finite points are
 * dropped first; never fewer than 3 points when the input has 3 or more.
 */
export function simplifyPoly(points, maxPoints = POLY_MAX_POINTS) {
  if (!Array.isArray(points)) return [];
  const pts = points.filter(isPoint).map((p) => [p[0], p[1]]);
  const limit = Math.max(3, Number.isInteger(maxPoints) ? maxPoints : POLY_MAX_POINTS);
  while (pts.length > limit) {
    let minIdx = 0;
    let minArea = Infinity;
    for (let i = 0; i < pts.length; i++) {
      const area = triangleArea(pts[(i - 1 + pts.length) % pts.length], pts[i], pts[(i + 1) % pts.length]);
      if (area < minArea) {
        minArea = area;
        minIdx = i;
      }
    }
    pts.splice(minIdx, 1);
  }
  return pts;
}

/** Is this a storable hold outline? Format, 3–16 points, finite, every coordinate in [0, 1]. */
export function validateHoldPoly(poly) {
  return typeof poly === "string" && HOLD_POLY_RE.test(poly);
}

/** Shoelace area, absolute; `0` for anything that is not a polygon. */
export function polyArea(points) {
  if (!isPointList(points, 3)) return 0;
  let sum = 0;
  for (let i = 0; i < points.length; i++) {
    const p = points[i];
    const q = points[(i + 1) % points.length];
    sum += p[0] * q[1] - q[0] * p[1];
  }
  const area = Math.abs(sum) / 2;
  return Number.isFinite(area) ? area : 0;
}

/**
 * The hold centre: the area-weighted (shoelace) centroid `[x, y]` of a point
 * array in one coordinate space. Area-weighted rather than the vertex mean
 * because a decoded mask outline crowds its vertices on the curved side, which
 * would pull a plain mean off the hold. Collinear points (zero area) fall back
 * to the vertex mean; invalid points are skipped; no valid point → `null`.
 */
export function polyCentroid(points) {
  if (!Array.isArray(points)) return null;
  const pts = points.filter(isPoint);
  if (pts.length === 0) return null;
  let twiceArea = 0;
  let cx = 0;
  let cy = 0;
  for (let i = 0; i < pts.length; i++) {
    const p = pts[i];
    const q = pts[(i + 1) % pts.length];
    const cross = p[0] * q[1] - q[0] * p[1];
    twiceArea += cross;
    cx += (p[0] + q[0]) * cross;
    cy += (p[1] + q[1]) * cross;
  }
  if (pts.length >= 3 && twiceArea !== 0 && Number.isFinite(twiceArea)) {
    const x = cx / (3 * twiceArea);
    const y = cy / (3 * twiceArea);
    if (Number.isFinite(x) && Number.isFinite(y)) return [x, y];
  }
  let sx = 0;
  let sy = 0;
  for (const p of pts) {
    sx += p[0];
    sy += p[1];
  }
  return [sx / pts.length, sy / pts.length];
}

function polyBounds(points) {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of points) {
    if (p[0] < minX) minX = p[0];
    if (p[0] > maxX) maxX = p[0];
    if (p[1] < minY) minY = p[1];
    if (p[1] > maxY) maxY = p[1];
  }
  return { minX, minY, maxX, maxY };
}

/** The x-intervals a horizontal line at `y` cuts out of a polygon (even–odd rule), sorted. */
function scanIntervals(points, y) {
  const xs = [];
  for (let i = 0; i < points.length; i++) {
    const p = points[i];
    const q = points[(i + 1) % points.length];
    // Half-open in y, so a vertex exactly on the line is counted once.
    if ((p[1] <= y) !== (q[1] <= y)) xs.push(p[0] + ((y - p[1]) * (q[0] - p[0])) / (q[1] - p[1]));
  }
  xs.sort((a, b) => a - b);
  return xs;
}

function intervalOverlap(xa, xb) {
  let i = 0;
  let j = 0;
  let len = 0;
  while (i + 1 < xa.length && j + 1 < xb.length) {
    const lo = Math.max(xa[i], xb[j]);
    const hi = Math.min(xa[i + 1], xb[j + 1]);
    if (hi > lo) len += hi - lo;
    if (xa[i + 1] < xb[j + 1]) i += 2;
    else j += 2;
  }
  return len;
}

function polyOverlapWithArea(a, areaA, boundsA, b, areaB, boundsB) {
  const x0 = Math.max(boundsA.minX, boundsB.minX);
  const x1 = Math.min(boundsA.maxX, boundsB.maxX);
  const y0 = Math.max(boundsA.minY, boundsB.minY);
  const y1 = Math.min(boundsA.maxY, boundsB.maxY);
  if (x1 <= x0 || y1 <= y0) return 0;
  const dy = (y1 - y0) / OVERLAP_SCANLINES;
  let inter = 0;
  for (let k = 0; k < OVERLAP_SCANLINES; k++) {
    const y = y0 + (k + 0.5) * dy;
    inter += intervalOverlap(scanIntervals(a, y), scanIntervals(b, y)) * dy;
  }
  inter = Math.min(inter, areaA, areaB);
  const union = areaA + areaB - inter;
  return union > 0 ? inter / union : 0;
}

/**
 * Intersection over union of two simple (possibly non-convex) polygons in
 * one coordinate space. The intersection is integrated over fixed scanlines
 * of the shared bounding box — exact along each line, so straight-edged
 * shapes come out within a fraction of a percent. A degenerate polygon gives 0.
 */
export function polyOverlap(a, b) {
  if (!isPointList(a, 3) || !isPointList(b, 3)) return 0;
  const areaA = polyArea(a);
  const areaB = polyArea(b);
  if (!(areaA > 0) || !(areaB > 0)) return 0;
  return polyOverlapWithArea(a, areaA, polyBounds(a), b, areaB, polyBounds(b));
}

// ── Spatial grid (private) ──

/** A uniform grid of point indices over the points' own bounds. */
function sprayGrid(points, cell) {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of points) {
    if (!p) continue;
    if (p[0] < minX) minX = p[0];
    if (p[0] > maxX) maxX = p[0];
    if (p[1] < minY) minY = p[1];
    if (p[1] > maxY) maxY = p[1];
  }
  if (minX === Infinity) return { cell, cols: 0, rows: 0, minX: 0, minY: 0, cells: [] };
  const cols = Math.floor((maxX - minX) / cell) + 1;
  const rows = Math.floor((maxY - minY) / cell) + 1;
  const cells = new Array(cols * rows);
  points.forEach((p, i) => {
    if (!p) return;
    const c = Math.floor((p[0] - minX) / cell);
    const r = Math.floor((p[1] - minY) / cell);
    const k = r * cols + c;
    (cells[k] || (cells[k] = [])).push(i);
  });
  return { cell, cols, rows, minX, minY, cells };
}

/** Calls `fn(index)` for every grid point in the square of half-size `radius` around (x, y). */
function sprayGridEach(grid, x, y, radius, fn) {
  if (grid.cols === 0) return;
  const c0 = Math.max(0, Math.floor((x - radius - grid.minX) / grid.cell));
  const c1 = Math.min(grid.cols - 1, Math.floor((x + radius - grid.minX) / grid.cell));
  const r0 = Math.max(0, Math.floor((y - radius - grid.minY) / grid.cell));
  const r1 = Math.min(grid.rows - 1, Math.floor((y + radius - grid.minY) / grid.cell));
  for (let r = r0; r <= r1; r++) {
    for (let c = c0; c <= c1; c++) {
      const bucket = grid.cells[r * grid.cols + c];
      if (bucket) for (const i of bucket) fn(i);
    }
  }
}

/** The nearest grid point within `radius` of (x, y), or -1. Ties go to the lower index. */
function sprayGridNearest(grid, points, x, y, radius) {
  let best = -1;
  let bestD = radius * radius;
  sprayGridEach(grid, x, y, radius, (i) => {
    const dx = points[i][0] - x;
    const dy = points[i][1] - y;
    const d = dx * dx + dy * dy;
    if (d < bestD || (d === bestD && (best === -1 || i < best))) {
      bestD = d;
      best = i;
    }
  });
  return best;
}

// ── Photo-to-photo transform ──

/**
 * The candidate pairs of the reset matching: for every old centroid, the new
 * centroids within `MAX_SHIFT_FRAC × diagPx` (the largest plausible shift),
 * nearest first, at most `PAIRS_PER_HOLD` of them. Pixels, both lists.
 */
export function candidatePairs(oldPts, newPts, diagPx) {
  if (!Array.isArray(oldPts) || !Array.isArray(newPts) || !(diagPx > 0) || !Number.isFinite(diagPx)) return [];
  const radius = MAX_SHIFT_FRAC * diagPx;
  const cleanNew = newPts.map((p) => (isPoint(p) ? p : null));
  const grid = sprayGrid(cleanNew, Math.max(16, radius / 3));
  const r2 = radius * radius;
  const out = [];
  oldPts.forEach((p, oldIdx) => {
    if (!isPoint(p)) return;
    const near = [];
    sprayGridEach(grid, p[0], p[1], radius, (newIdx) => {
      const q = cleanNew[newIdx];
      const d = (q[0] - p[0]) ** 2 + (q[1] - p[1]) ** 2;
      if (d <= r2) near.push([d, newIdx]);
    });
    near.sort((a, b) => a[0] - b[0] || a[1] - b[1]);
    for (const [, newIdx] of near.slice(0, PAIRS_PER_HOLD)) {
      const q = cleanNew[newIdx];
      out.push({ oldIdx, newIdx, from: [p[0], p[1]], to: [q[0], q[1]] });
    }
  });
  return out;
}

/** Seeded mulberry32: a tiny, well-mixed 32-bit generator — reproducible, unlike `Math.random`. */
function sprayPrng(seed) {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Least-squares similarity `p' = s·R·p + t` (closed form) over index pairs; null when degenerate. */
function fitSimilarity(src, dst, pairs) {
  const n = pairs.length;
  if (n < 2) return null;
  let mx = 0;
  let my = 0;
  let nx = 0;
  let ny = 0;
  for (const [i, j] of pairs) {
    mx += src[i][0];
    my += src[i][1];
    nx += dst[j][0];
    ny += dst[j][1];
  }
  mx /= n;
  my /= n;
  nx /= n;
  ny /= n;
  let sxx = 0;
  let a = 0;
  let b = 0;
  for (const [i, j] of pairs) {
    const dx = src[i][0] - mx;
    const dy = src[i][1] - my;
    const ex = dst[j][0] - nx;
    const ey = dst[j][1] - ny;
    sxx += dx * dx + dy * dy;
    a += dx * ex + dy * ey;
    b += dx * ey - dy * ex;
  }
  if (!(sxx > 0)) return null;
  a /= sxx;
  b /= sxx;
  return { a, b, tx: nx - (a * mx - b * my), ty: ny - (b * mx + a * my) };
}

function transformWithinBounds(scale, rot, slack) {
  return scale >= MIN_TRANSFORM_SCALE / slack && scale <= MAX_TRANSFORM_SCALE * slack && Math.abs(rot) <= MAX_TRANSFORM_ROT * slack;
}

/** The old points that land on a distinct new point within `INLIER_PX`, as `[oldIdx, newIdx]` pairs. */
function collectInliers(model, src, dst, grid, centre, radius) {
  const used = new Set();
  const pairs = [];
  let residual = 0;
  let considered = 0;
  const r2 = radius * radius;
  for (let i = 0; i < src.length; i++) {
    const p = src[i];
    if (centre && (p[0] - centre[0]) ** 2 + (p[1] - centre[1]) ** 2 > r2) continue;
    considered++;
    const x = model.a * p[0] - model.b * p[1] + model.tx;
    const y = model.b * p[0] + model.a * p[1] + model.ty;
    const j = sprayGridNearest(grid, dst, x, y, INLIER_PX);
    if (j === -1 || used.has(j)) continue;
    used.add(j);
    pairs.push([i, j]);
    residual += Math.hypot(dst[j][0] - x, dst[j][1] - y);
  }
  return { pairs, residual, considered };
}

/**
 * The photo-to-photo transform from centroid pairs: `{ tx, ty, scale, rot,
 * inliers }` in pixels (`p' = scale·R(rot)·p + (tx, ty)`, origin top-left),
 * or `null` when there is no consensus.
 *
 * RANSAC with a 2-pair minimal sample, sampled from the neighbourhood: an
 * anchor's candidate partner fixes the shift, the anchor's nearest old
 * neighbour and a new point at a plausible distance and angle fix scale and
 * rotation. A hypothesis must first agree with the anchor's other neighbours
 * (cheap), then it is refitted by least squares on its inliers over a
 * doubling radius, so a short, noisy baseline grows into a whole-wall fit.
 * A random pair sample would almost never pick two true pairs when every old
 * hold has dozens of candidates; the neighbourhood makes the second pair
 * nearly free once the first is right. The seeded generator picks the
 * anchors; the search stops once `CONFIRMATIONS` anchors grew the same
 * model, or after `RANSAC_ITERS` hypotheses. The best model is the one with
 * the most inliers (then the smallest residual).
 */
export function estimateSimilarity(pairs) {
  if (!Array.isArray(pairs) || pairs.length < MIN_INLIERS) return null;

  // Distinct old and new points; candidates per old point.
  const src = [];
  const dst = [];
  const srcKey = new Map();
  const dstKey = new Map();
  const cands = [];
  for (const pr of pairs) {
    if (!pr || !isPoint(pr.from) || !isPoint(pr.to)) continue;
    const fk = `${pr.from[0]},${pr.from[1]}`;
    const tk = `${pr.to[0]},${pr.to[1]}`;
    let i = srcKey.get(fk);
    if (i === undefined) {
      i = src.length;
      srcKey.set(fk, i);
      src.push([pr.from[0], pr.from[1]]);
      cands.push([]);
    }
    let j = dstKey.get(tk);
    if (j === undefined) {
      j = dst.length;
      dstKey.set(tk, j);
      dst.push([pr.to[0], pr.to[1]]);
    }
    if (!cands[i].includes(j)) cands[i].push(j);
  }
  const minNeeded = Math.max(MIN_INLIERS, Math.ceil(MIN_INLIER_FRAC * src.length));
  if (src.length < minNeeded || dst.length < MIN_INLIERS) return null;

  const dstBounds = polyBounds(dst);
  const spacing = Math.sqrt(Math.max(1, (dstBounds.maxX - dstBounds.minX) * (dstBounds.maxY - dstBounds.minY)) / dst.length);
  const grid = sprayGrid(dst, Math.max(2 * INLIER_PX, spacing));
  const srcBounds = polyBounds(src);
  const srcDiag = Math.hypot(srcBounds.maxX - srcBounds.minX, srcBounds.maxY - srcBounds.minY);

  // Anchors: a seeded shuffle of the old points.
  const rand = sprayPrng(RANSAC_SEED);
  const order = src.map((_, i) => i);
  for (let i = order.length - 1; i > 0; i--) {
    const k = Math.floor(rand() * (i + 1));
    [order[i], order[k]] = [order[k], order[i]];
  }
  const anchors = order.slice(0, ANCHOR_COUNT);

  let best = null;
  let tried = 0;
  for (const ai of anchors) {
    const a = src[ai];
    const neighbours = src
      .map((p, i) => [Math.hypot(p[0] - a[0], p[1] - a[1]), i])
      .filter(([d, i]) => i !== ai && d > 0)
      .sort((x, y) => x[0] - y[0] || x[1] - y[1]);
    const local = neighbours.slice(0, LOCAL_NEIGHBORS).map(([, i]) => i);
    const bases = neighbours.filter(([d]) => d >= MIN_BASELINE_PX).slice(0, 2);
    const localMin = Math.min(LOCAL_MIN, local.length);
    for (const aj of cands[ai]) {
      const a2 = dst[aj];
      let localBest = null;
      for (const [baseLen, bi] of bases) {
        const b = src[bi];
        const angB = Math.atan2(b[1] - a[1], b[0] - a[0]);
        const rMax = baseLen * MAX_TRANSFORM_SCALE * 1.1;
        const rMin = baseLen * MIN_TRANSFORM_SCALE * 0.9;
        const options = [];
        sprayGridEach(grid, a2[0], a2[1], rMax, (bj) => {
          if (bj === aj) return;
          const q = dst[bj];
          const len = Math.hypot(q[0] - a2[0], q[1] - a2[1]);
          if (len < rMin || len > rMax) return;
          let dAng = Math.atan2(q[1] - a2[1], q[0] - a2[0]) - angB;
          dAng = Math.atan2(Math.sin(dAng), Math.cos(dAng));
          if (Math.abs(dAng) <= MAX_TRANSFORM_ROT * 1.1) options.push(bj);
        });
        options.sort((x, y) => x - y);
        for (const bj of options) {
          if (++tried > RANSAC_ITERS) break;
          const model = fitSimilarity(src, dst, [[ai, aj], [bi, bj]]);
          if (!model || !transformWithinBounds(Math.hypot(model.a, model.b), Math.atan2(model.b, model.a), 1.1)) continue;
          let agree = 0;
          for (const li of local) {
            const p = src[li];
            const x = model.a * p[0] - model.b * p[1] + model.tx;
            const y = model.b * p[0] + model.a * p[1] + model.ty;
            if (sprayGridNearest(grid, dst, x, y, INLIER_PX) !== -1) agree++;
          }
          if (agree < localMin) continue;
          if (!localBest || agree > localBest.agree) localBest = { model, agree, baseLen };
        }
        if (tried > RANSAC_ITERS) break;
      }
      if (!localBest) {
        if (tried > RANSAC_ITERS) break;
        continue;
      }
      // Local optimisation, once per (anchor, partner): the second pairs of one
      // shift converge to the same model, so only the best-agreeing one is grown.
      let m = localBest.model;
      let radius = Math.max(3 * localBest.baseLen, 2 * spacing);
      let losing = false;
      for (;;) {
        const covered = radius >= srcDiag;
        const inl = collectInliers(m, src, dst, grid, covered ? null : a, radius);
        // A wrong shift agrees by chance near the anchor only: once its inlier
        // share falls well below the leader's, growing it further is wasted.
        if (best && inl.considered >= LOSING_MIN_POINTS && inl.pairs.length < LOSING_SHARE * (best.count / src.length) * inl.considered) {
          losing = true;
          break;
        }
        const refit = inl.pairs.length >= 2 ? fitSimilarity(src, dst, inl.pairs) : null;
        if (!refit) break;
        m = refit;
        if (covered) break;
        radius *= 2;
      }
      if (losing) continue;
      const fin = collectInliers(m, src, dst, grid, null, 0);
      const count = fin.pairs.length;
      const close = best !== null && modelsAgree(m, best.model, srcBounds);
      if (!best || count > best.count || (count === best.count && fin.residual < best.residual)) {
        best = { model: m, count, residual: fin.residual, confirmedBy: close ? best.confirmedBy : new Set() };
      }
      if (close || best.model === m) best.confirmedBy.add(ai);
      if (tried > RANSAC_ITERS) break;
    }
    if (tried > RANSAC_ITERS) break;
    // Independent anchors grew the same model: further anchors would only repeat it.
    if (best && best.confirmedBy.size >= CONFIRMATIONS) break;
  }

  if (!best || best.count < minNeeded) return null;
  const scale = Math.hypot(best.model.a, best.model.b);
  const rot = Math.atan2(best.model.b, best.model.a);
  if (![scale, rot, best.model.tx, best.model.ty].every(Number.isFinite)) return null;
  if (!transformWithinBounds(scale, rot, 1)) return null;
  return { tx: best.model.tx, ty: best.model.ty, scale, rot, inliers: best.count };
}

/** Do two models put the corners of the old holds' bounding box within `INLIER_PX` of each other? */
function modelsAgree(m1, m2, bounds) {
  const corners = [[bounds.minX, bounds.minY], [bounds.maxX, bounds.minY], [bounds.minX, bounds.maxY], [bounds.maxX, bounds.maxY]];
  return corners.every(([x, y]) => Math.hypot(m1.a * x - m1.b * y + m1.tx - (m2.a * x - m2.b * y + m2.tx), m1.b * x + m1.a * y + m1.ty - (m2.b * x + m2.a * y + m2.ty)) <= INLIER_PX);
}

const IDENTITY = Object.freeze({ tx: 0, ty: 0, scale: 1, rot: 0 });

/** A usable transform, or the identity for a missing / non-finite one. */
function transformOrIdentity(t) {
  if (!t || ![t.tx, t.ty, t.scale, t.rot].every(Number.isFinite)) return IDENTITY;
  return t;
}

function sizeOf(size) {
  const w = size?.width;
  const h = size?.height;
  if (!(w > 0) || !(h > 0) || !Number.isFinite(w) || !Number.isFinite(h)) throw new Error("invalidSize");
  return { w, h };
}

/** Normalized (old photo) → pixel (new photo) point mapper. */
function oldToNewPx(transform, from) {
  const t = transformOrIdentity(transform);
  const ca = t.scale * Math.cos(t.rot);
  const sa = t.scale * Math.sin(t.rot);
  return (p) => {
    const x = p[0] * from.w;
    const y = p[1] * from.h;
    return [ca * x - sa * y + t.tx, sa * x + ca * y + t.ty];
  };
}

const clampUnit = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);

/**
 * Carries holds over to a new photo: denormalize with `fromSize`, apply the
 * pixel transform, renormalize with `toSize`, clip to [0, 1], re-encode.
 * `x`, `y` become the transformed centre; every other field is kept, the
 * hold id above all (problems point at it). A hold whose `poly` does not
 * decode keeps its `poly` text unchanged.
 */
export function applyHoldTransform(holds, transform, fromSize, toSize) {
  if (!Array.isArray(holds)) return [];
  const from = sizeOf(fromSize);
  const to = sizeOf(toSize);
  const map = oldToNewPx(transform, from);
  const toNorm = (q) => [clampUnit(q[0] / to.w), clampUnit(q[1] / to.h)];
  return holds.map((hold) => {
    if (!hold || typeof hold !== "object") return hold;
    const out = { ...hold };
    if (Number.isFinite(hold.x) && Number.isFinite(hold.y)) {
      const c = toNorm(map([hold.x, hold.y]));
      out.x = round4(c[0]);
      out.y = round4(c[1]);
    }
    const pts = decodePoly(hold.poly);
    if (pts && pts.length >= 3) out.poly = encodePoly(simplifyPoly(pts.map((p) => toNorm(map(p)))));
    return out;
  });
}

/**
 * Pairs old holds with the proposals of a new photo, 1:1, greedily by
 * outline IoU (highest first), at or above `MATCH_IOU`, in new-photo pixels.
 * A bounding-box grid prefilter keeps it near-linear: IoU ≥ MATCH_IOU needs
 * overlapping boxes, so the filter drops no possible match.
 */
export function matchHolds(oldHolds, proposals, transform, fromSize, toSize) {
  const olds = Array.isArray(oldHolds) ? oldHolds : [];
  const props = Array.isArray(proposals) ? proposals : [];
  const from = sizeOf(fromSize);
  const to = sizeOf(toSize);
  const map = oldToNewPx(transform, from);

  const shape = (pts) => {
    if (!pts || pts.length < 3) return null;
    const area = polyArea(pts);
    return area > 0 ? { pts, area, bounds: polyBounds(pts) } : null;
  };
  const oldShapes = olds.map((h) => shape(decodePoly(h?.poly)?.map(map)));
  const propShapes = props.map((p) => shape(decodePoly(p?.poly)?.map((q) => [q[0] * to.w, q[1] * to.h])));

  // Proposal boxes in a uniform grid; the cell is the typical proposal size.
  const sizes = propShapes.filter(Boolean).map((s) => Math.max(s.bounds.maxX - s.bounds.minX, s.bounds.maxY - s.bounds.minY)).sort((a, b) => a - b);
  const cell = Math.max(8, sizes.length ? sizes[Math.floor(sizes.length / 2)] : 64);
  const buckets = new Map();
  const cellRange = (b) => [Math.floor(b.minX / cell), Math.floor(b.maxX / cell), Math.floor(b.minY / cell), Math.floor(b.maxY / cell)];
  propShapes.forEach((s, pi) => {
    if (!s) return;
    const [c0, c1, r0, r1] = cellRange(s.bounds);
    for (let r = r0; r <= r1; r++) {
      for (let c = c0; c <= c1; c++) {
        const k = `${c},${r}`;
        (buckets.get(k) || buckets.set(k, []).get(k)).push(pi);
      }
    }
  });

  const edges = [];
  oldShapes.forEach((s, hi) => {
    if (!s || typeof olds[hi]?.id !== "string") return;
    const [c0, c1, r0, r1] = cellRange(s.bounds);
    const seen = new Set();
    for (let r = r0; r <= r1; r++) {
      for (let c = c0; c <= c1; c++) {
        for (const pi of buckets.get(`${c},${r}`) || []) {
          if (seen.has(pi)) continue;
          seen.add(pi);
          const p = propShapes[pi];
          if (p.bounds.maxX <= s.bounds.minX || p.bounds.minX >= s.bounds.maxX || p.bounds.maxY <= s.bounds.minY || p.bounds.minY >= s.bounds.maxY) continue;
          const iou = polyOverlapWithArea(s.pts, s.area, s.bounds, p.pts, p.area, p.bounds);
          if (iou >= MATCH_IOU) edges.push([iou, hi, pi]);
        }
      }
    }
  });
  edges.sort((x, y) => y[0] - x[0] || x[1] - y[1] || x[2] - y[2]);

  const holdTaken = new Set();
  const propTaken = new Set();
  const matches = [];
  for (const [iou, hi, pi] of edges) {
    if (holdTaken.has(hi) || propTaken.has(pi)) continue;
    holdTaken.add(hi);
    propTaken.add(pi);
    matches.push({ holdId: olds[hi].id, proposalIdx: pi, iou });
  }
  const unmatchedHoldIds = [];
  olds.forEach((h, hi) => {
    if (!holdTaken.has(hi) && typeof h?.id === "string") unmatchedHoldIds.push(h.id);
  });
  const newProposalIdxs = [];
  props.forEach((_, pi) => {
    if (!propTaken.has(pi)) newProposalIdxs.push(pi);
  });
  return { matches, unmatchedHoldIds, newProposalIdxs };
}

const HOLD_ID_RE = /^h([1-9]\d*)$/;

/**
 * Appends new holds with fresh ids `h{seq}`. Ids on the input are ignored and
 * never reused: the sequence only grows (it also starts above every existing
 * id, should a stored counter ever lag behind), so a deleted hold's id stays
 * dead and old problems cannot silently point at a new hold.
 */
export function appendHolds(holds, nextHoldSeq, newHolds) {
  const base = Array.isArray(holds) ? holds.filter((h) => h && typeof h === "object") : [];
  const adds = Array.isArray(newHolds) ? newHolds.filter((h) => h && typeof h === "object") : [];
  if (base.length + adds.length > MAX_SPRAY_HOLDS) throw new Error("tooManyHolds");
  let seq = Number.isInteger(nextHoldSeq) && nextHoldSeq >= 1 ? nextHoldSeq : 1;
  for (const h of base) {
    const m = typeof h.id === "string" ? HOLD_ID_RE.exec(h.id) : null;
    if (m && Number(m[1]) >= seq) seq = Number(m[1]) + 1;
  }
  const out = base.map((h) => ({ ...h }));
  for (const h of adds) {
    out.push({ ...h, id: `h${seq}` });
    seq += 1;
  }
  return { holds: out, nextHoldSeq: seq };
}

// ── Smart marking ──

function pointInPolygon(points, x, y) {
  let inside = false;
  for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
    const [xi, yi] = points[i];
    const [xj, yj] = points[j];
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

function distanceToOutline(points, x, y) {
  let best = Infinity;
  for (let i = 0; i < points.length; i++) {
    const [x1, y1] = points[i];
    const [x2, y2] = points[(i + 1) % points.length];
    const dx = x2 - x1;
    const dy = y2 - y1;
    const len2 = dx * dx + dy * dy;
    const t = len2 > 0 ? Math.max(0, Math.min(1, ((x - x1) * dx + (y - y1) * dy) / len2)) : 0;
    const d = Math.hypot(x - (x1 + t * dx), y - (y1 + t * dy));
    if (d < best) best = d;
  }
  return best;
}

/**
 * The candidates of a smart-marking tap, best first. Near-identical
 * detections collapse to the most confident one; then every detection that
 * contains the tap, SMALLEST first (a small hold sitting on a volume wins
 * over the volume); if none contains it, the single nearest one within
 * `nearPx` of its outline; otherwise none.
 */
export function pickCandidates(detections, point, { dupIoU = 0.7, nearPx = 8 } = {}) {
  if (!Array.isArray(detections) || !isPoint(point)) return [];
  const items = [];
  detections.forEach((d, idx) => {
    if (!d || !isPointList(d.poly, 3)) return;
    const area = polyArea(d.poly);
    if (!(area > 0)) return;
    const score = Number.isFinite(d.score) ? d.score : 0;
    items.push({ d, idx, area, score, bounds: polyBounds(d.poly) });
  });
  items.sort((a, b) => b.score - a.score || a.idx - b.idx);
  const kept = [];
  for (const it of items) {
    const twin = kept.some((k) => polyOverlapWithArea(it.d.poly, it.area, it.bounds, k.d.poly, k.area, k.bounds) >= dupIoU);
    if (!twin) kept.push(it);
  }
  const copy = (it) => ({ ...it.d, poly: it.d.poly.map((p) => [p[0], p[1]]) });

  const [x, y] = point;
  const containing = kept.filter((it) => pointInPolygon(it.d.poly, x, y));
  if (containing.length > 0) {
    containing.sort((a, b) => a.area - b.area || b.score - a.score || a.idx - b.idx);
    return containing.map(copy);
  }
  let nearest = null;
  let nearestD = Infinity;
  for (const it of kept) {
    const dist = distanceToOutline(it.d.poly, x, y);
    if (dist < nearestD || (dist === nearestD && nearest && (it.score > nearest.score || (it.score === nearest.score && it.idx < nearest.idx)))) {
      nearest = it;
      nearestD = dist;
    }
  }
  return nearest && nearestD <= nearPx ? [copy(nearest)] : [];
}

/**
 * Drops the candidates that are near-identical to a hold already on the wall
 * (or a proposal still under review), so a tap on an empty spot never adds a
 * duplicate and "search inside" skips the selected hold itself. Both lists in
 * one coordinate space; the surviving candidate objects are returned as-is.
 */
export function withoutExistingTwins(candidates, holds, iou = 0.7) {
  if (!Array.isArray(candidates)) return [];
  const existing = (Array.isArray(holds) ? holds : []).filter((h) => h && isPointList(h.poly, 3));
  return candidates.filter((c) => c && isPointList(c.poly, 3) && !existing.some((h) => polyOverlap(c.poly, h.poly) >= iou));
}

// ── Wall outline ──
// A polygon around the climbable wall on a version's photo; the app masks
// everything outside it when it draws (the photo file is never changed).
// docs/spec/02d-shared-spraywall.md, the wall-outline rows.

/** The most points of a stored wall outline: a wall is angular, so more than a hold's 16. */
export const OUTLINE_MAX_POINTS = 64;
// The automatic outline uses only half of them, so a hand correction has room to add detail.
const OUTLINE_AUTO_POINTS = 32;
/** The smallest stored outline, as a fraction of the photo area: catches an outline collapsed by accident. */
export const OUTLINE_MIN_AREA = 0.05;
// The stored format, checked textually: 3–64 points, the hold outline's number format.
const OUTLINE_RE = new RegExp(`^${POLY_PAIR}(?:;${POLY_PAIR}){2,${OUTLINE_MAX_POINTS - 1}}$`);
// Starting values of the automatic outline, to be tuned on the hold-detector
// test walls: fewer holds than this give no outline; holds closer than
// GAP × the median hold size form one group; the hull grows by MARGIN × that size.
const OUTLINE_MIN_HOLDS = 5;
const OUTLINE_GAP_FACTOR = 4;
const OUTLINE_MARGIN_FACTOR = 1.5;
// The margin is a Minkowski sum with an octagon: the hull stays convex.
const OUTLINE_MARGIN_DIRS = Array.from({ length: 8 }, (_, i) => [Math.cos((i * Math.PI) / 4), Math.sin((i * Math.PI) / 4)]);

/** Is this a storable wall outline? The hold-outline format with 3–64 points, covering at least 5 % of the photo. */
export function isOutlinePoly(str) {
  if (typeof str !== "string" || !OUTLINE_RE.test(str)) return false;
  return polyArea(decodePoly(str)) >= OUTLINE_MIN_AREA;
}

/** Monotone-chain convex hull, counter-clockwise in a y-down space; collinear points dropped. */
function convexHull(points) {
  const pts = points.filter(isPoint).map((p) => [p[0], p[1]]).sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  if (pts.length < 3) return pts;
  const cross = (o, a, b) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
  const lower = [];
  for (const p of pts) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) lower.pop();
    lower.push(p);
  }
  const upper = [];
  for (let i = pts.length - 1; i >= 0; i--) {
    const p = pts[i];
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) upper.pop();
    upper.push(p);
  }
  lower.pop();
  upper.pop();
  return lower.concat(upper);
}

/** Sutherland–Hodgman clip of a convex polygon to the rectangle [0, w] × [0, h]. */
function clipToRect(points, w, h) {
  const edges = [
    { inside: (p) => p[0] >= 0, cut: (p, q) => { const t = (0 - p[0]) / (q[0] - p[0]); return [0, p[1] + t * (q[1] - p[1])]; } },
    { inside: (p) => p[0] <= w, cut: (p, q) => { const t = (w - p[0]) / (q[0] - p[0]); return [w, p[1] + t * (q[1] - p[1])]; } },
    { inside: (p) => p[1] >= 0, cut: (p, q) => { const t = (0 - p[1]) / (q[1] - p[1]); return [p[0] + t * (q[0] - p[0]), 0]; } },
    { inside: (p) => p[1] <= h, cut: (p, q) => { const t = (h - p[1]) / (q[1] - p[1]); return [p[0] + t * (q[0] - p[0]), h]; } },
  ];
  let out = points;
  for (const edge of edges) {
    const input = out;
    out = [];
    for (let i = 0; i < input.length; i++) {
      const cur = input[i];
      const prev = input[(i - 1 + input.length) % input.length];
      const curIn = edge.inside(cur);
      const prevIn = edge.inside(prev);
      if (curIn) {
        if (!prevIn) out.push(edge.cut(prev, cur));
        out.push(cur);
      } else if (prevIn) {
        out.push(edge.cut(prev, cur));
      }
    }
    if (out.length === 0) return out;
  }
  return out;
}

/** The gap between two boxes: the distance between the rectangles, 0 when they touch or overlap. */
function boxGap(a, b) {
  const dx = Math.max(0, b.minX - a.maxX, a.minX - b.maxX);
  const dy = Math.max(0, b.minY - a.maxY, a.minY - b.maxY);
  return Math.hypot(dx, dy);
}

/** The lower median of a non-empty number list. */
function lowerMedian(values) {
  const sorted = [...values].sort((a, b) => a - b);
  return sorted[Math.floor((sorted.length - 1) / 2)];
}

/** Pixel polygon → stored outline string (at most `maxPoints` points), or null when not storable. */
function encodeOutlinePx(pxPoints, w, h, maxPoints = OUTLINE_MAX_POINTS) {
  if (!Array.isArray(pxPoints) || pxPoints.length < 3) return null;
  const simplified = simplifyPoly(pxPoints, maxPoints);
  const out = encodePoly(simplified.map((p) => [clampUnit(p[0] / w), clampUnit(p[1] / h)]));
  return isOutlinePoly(out) ? out : null;
}

/**
 * The automatic wall outline from holds or detection proposals (anything with
 * a normalized `poly`): the largest connected cluster of holds, its convex
 * hull grown by a margin, clipped to the photo, at most 32 points (half of the
 * stored limit, so a hand correction has room). `null` for
 * fewer than 5 usable holds, an unusable size or an unstorable result.
 */
export function wallOutlineFromHolds(holds, size) {
  let dims;
  try {
    dims = sizeOf(size);
  } catch {
    return null;
  }
  const { w, h } = dims;
  const shapes = [];
  for (const hold of Array.isArray(holds) ? holds : []) {
    if (!hold || typeof hold !== "object" || hold.missing === true) continue;
    const pts = decodePoly(hold.poly);
    if (!pts || pts.length < 3) continue;
    const px = pts.map((p) => [p[0] * w, p[1] * h]);
    const box = polyBounds(px);
    shapes.push({ px, box, size: Math.max(box.maxX - box.minX, box.maxY - box.minY), area: polyArea(px) });
  }
  if (shapes.length < OUTLINE_MIN_HOLDS) return null;
  const m = lowerMedian(shapes.map((s) => s.size));
  const gapLimit = OUTLINE_GAP_FACTOR * m;

  // Union–find over every pair whose boxes lie closer than the gap limit.
  const parent = shapes.map((_, i) => i);
  const find = (i) => {
    while (parent[i] !== i) {
      parent[i] = parent[parent[i]];
      i = parent[i];
    }
    return i;
  };
  for (let i = 0; i < shapes.length; i++) {
    for (let j = i + 1; j < shapes.length; j++) {
      if (boxGap(shapes[i].box, shapes[j].box) < gapLimit) {
        const a = find(i);
        const b = find(j);
        // The lower index stays the root, so the tie rule below reads the root.
        if (a !== b) parent[Math.max(a, b)] = Math.min(a, b);
      }
    }
  }
  const areaByRoot = new Map();
  shapes.forEach((s, i) => {
    const r = find(i);
    areaByRoot.set(r, (areaByRoot.get(r) || 0) + s.area);
  });
  let wallRoot = -1;
  let wallArea = -1;
  // Roots in index order: a tie keeps the group of the lower hold index.
  for (const [r, area] of [...areaByRoot.entries()].sort((a, b) => a[0] - b[0])) {
    if (area > wallArea) {
      wallRoot = r;
      wallArea = area;
    }
  }
  const groupPoints = [];
  shapes.forEach((s, i) => {
    if (find(i) === wallRoot) groupPoints.push(...s.px);
  });
  const hull = convexHull(groupPoints);
  if (hull.length < 3) return null;
  const margin = OUTLINE_MARGIN_FACTOR * m;
  const grown = convexHull(hull.flatMap((p) => OUTLINE_MARGIN_DIRS.map((d) => [p[0] + d[0] * margin, p[1] + d[1] * margin])));
  const clipped = clipToRect(grown, w, h);
  return encodeOutlinePx(clipped, w, h, OUTLINE_AUTO_POINTS);
}

/**
 * Carries a wall outline over to a new photo with the photo-swap transform —
 * the math of `applyHoldTransform`. `null` when the input is not a stored
 * outline, a size is unusable or the moved outline is no longer storable.
 */
export function applyOutlineTransform(outline, transform, fromSize, toSize) {
  if (!isOutlinePoly(outline)) return null;
  let from;
  let to;
  try {
    from = sizeOf(fromSize);
    to = sizeOf(toSize);
  } catch {
    return null;
  }
  const map = oldToNewPx(transform, from);
  const moved = decodePoly(outline).map((p) => {
    const q = map(p);
    return [clampUnit(q[0] / to.w) * to.w, clampUnit(q[1] / to.h) * to.h];
  });
  return encodeOutlinePx(moved, to.w, to.h);
}

/** Does the hold's centre lie outside the wall outline? `false` without a stored outline or a centre. */
export function holdOutsideOutline(hold, outline) {
  if (!hold || typeof hold !== "object" || !isOutlinePoly(outline)) return false;
  let centre = Number.isFinite(hold.x) && Number.isFinite(hold.y) ? [hold.x, hold.y] : null;
  if (!centre) {
    const pts = decodePoly(hold.poly);
    centre = pts && pts.length >= 3 ? polyCentroid(pts) : null;
  }
  if (!centre) return false;
  return !pointInPolygon(decodePoly(outline), centre[0], centre[1]);
}

// ── Hold editing ──

/**
 * The tap rule of the hold editor (D74): of the items whose outline contains
 * `point`, the one with the SMALLEST area wins — a small hold sitting on a
 * volume is selected by tapping it, the volume by tapping outside the hold.
 * Shared rather than client code because it is the same rule `pickCandidates`
 * ranks by, on the same module-private point-in-polygon test; a client copy
 * would be a second `pointInPolygon` (`noNewTwins`).
 *
 * `items` = `[{ points, … }]` (`points` a point array), `point` = `[x, y]`,
 * both in ONE coordinate space. Equal areas → the lower index. An item for
 * which `options.eligible(item) === false` is skipped; an item without a
 * real outline (fewer than 3 points, zero area) is never a hit. Returns the
 * item itself, or `null` on bad input or no hit.
 */
export function holdAtPoint(items, point, options = {}) {
  if (!Array.isArray(items) || !isPoint(point)) return null;
  const eligible = options && typeof options.eligible === "function" ? options.eligible : null;
  const [x, y] = point;
  let best = null;
  let bestArea = Infinity;
  for (const item of items) {
    if (!item || typeof item !== "object" || !isPointList(item.points, 3)) continue;
    if (eligible && eligible(item) === false) continue;
    const area = polyArea(item.points);
    if (!(area > 0) || area >= bestArea) continue;
    if (pointInPolygon(item.points, x, y)) {
      best = item;
      bestArea = area;
    }
  }
  return best;
}

const EDIT_ADD_SOURCES = new Set(["pick", "manual"]);
// A removed hold comes back as what it was, so a detected (`auto`) one too.
const RESTORE_SOURCES = new Set(["auto", "pick", "manual"]);

/** The numeric part of an `h<n>` hold id, or `null`. */
function holdSeqOf(id) {
  const m = typeof id === "string" ? HOLD_ID_RE.exec(id) : null;
  return m ? Number(m[1]) : null;
}

/**
 * The stored centre of an outline: the polygon centroid, normalized, 4
 * decimals. One rule for `add`, `setPoly` and `restore`, so a hold's centre
 * never depends on which operation last touched its outline.
 */
function holdCentreOf(poly) {
  const centre = polyCentroid(decodePoly(poly)) || [0, 0];
  return { x: round4(centre[0]), y: round4(centre[1]) };
}

/**
 * The one composition of a hold edit (`editSprayHolds`, D39 / D74): the
 * callable runs it inside its transaction (and its dry run), and the hold
 * editor re-runs it on the server's answer (`usedIds` = the response's
 * `missing`) to update its local copy deterministically instead of re-reading
 * a version document of ~420 KB. One function, so the two cannot disagree on
 * what removing a used hold does.
 *
 * `edits = { restore = [], add = [], remove = [], setPoly = {}, setFeetOnly = {}, setNote = {} }`;
 * `usedIds` — a `Set` or an array of the hold ids any problem of the version
 * uses (`usedHoldIds`). Applied in this order:
 * - `restore` (`[{ id, src?, poly?, feetOnly?, note? }]`) — a `missing` hold
 *   loses the flag and keeps everything else (the other fields are ignored);
 *   a hold that was dropped comes back with the SAME id, which must be
 *   `h<n>` with `n` below the counter: the counter never goes back, so such
 *   an id was handed out once and can never collide with a new one. It needs
 *   `src` and `poly`; it is put back at its id's place in the list. Undo of a
 *   removal therefore leaves problems pointing at the very hold they used.
 * - `remove` (a used hold becomes `missing: true` and stays, an unused one is
 *   dropped);
 * - `setPoly` (`{ id: poly }`, the outline of a live hold; the centre follows);
 * - `setFeetOnly` (`false` deletes the key);
 * - `setNote` (a blank note deletes the key);
 * - `add` (centre = the outline's centroid, ids from `appendHolds` — never one
 *   a removed hold had).
 * An id may be the target of only one of `restore`, `remove` and the
 * per-hold edits, so every check reads the holds as they came in.
 * Never mutates its input.
 *
 * Throws `new Error(code)`, checked in this order: `emptyEdit`,
 * `invalidHoldId`, `holdMissing`, `holdNotRemoved`, `invalidPoly` (restore,
 * then setPoly, then add), `invalidNote` (also for a note over 40
 * characters), `tooManyHolds` (restored holds count toward the ceiling too).
 *
 * @returns {{ holds: object[], nextHoldSeq: number, addedIds: string[], missingIds: string[], removedIds: string[] }}
 */
export function applyHoldEdits(holds, nextHoldSeq, edits, usedIds) {
  const e = edits && typeof edits === "object" && !Array.isArray(edits) ? edits : {};
  const isMap = (v) => v !== null && typeof v === "object" && !Array.isArray(v);
  const restore = e.restore === undefined ? [] : e.restore;
  const add = e.add === undefined ? [] : e.add;
  const remove = e.remove === undefined ? [] : e.remove;
  const setPoly = e.setPoly === undefined ? {} : e.setPoly;
  const setFeetOnly = e.setFeetOnly === undefined ? {} : e.setFeetOnly;
  const setNote = e.setNote === undefined ? {} : e.setNote;
  // A malformed container is an attempted operation (so not `emptyEdit`); its
  // own code is raised at its place in the documented order below.
  const restoreOk = Array.isArray(restore);
  const addOk = Array.isArray(add);
  const removeOk = Array.isArray(remove);
  const polyOk = isMap(setPoly);
  const feetOk = isMap(setFeetOnly);
  const noteOk = isMap(setNote);
  const polyIds = polyOk ? Object.keys(setPoly) : [];
  const feetIds = feetOk ? Object.keys(setFeetOnly) : [];
  const noteIds = noteOk ? Object.keys(setNote) : [];
  const opCount = (restoreOk ? restore.length : 1) + (addOk ? add.length : 1) + (removeOk ? remove.length : 1)
    + (polyOk ? polyIds.length : 1) + (feetOk ? feetIds.length : 1) + (noteOk ? noteIds.length : 1);
  if (opCount === 0) throw new Error("emptyEdit");
  if (!restoreOk || !removeOk || !polyOk || !feetOk) throw new Error("invalidHoldId");

  const base = Array.isArray(holds) ? holds.filter((h) => h && typeof h === "object") : [];
  const byId = new Map();
  for (const h of base) if (typeof h.id === "string" && !byId.has(h.id)) byId.set(h.id, h);

  // The id floor comes from the ORIGINAL holds, so a dropped id is never handed
  // out again — and a restored one must lie below it.
  let seq = Number.isInteger(nextHoldSeq) && nextHoldSeq >= 1 ? nextHoldSeq : 1;
  for (const h of base) {
    const n = holdSeqOf(h.id);
    if (n !== null && n >= seq) seq = n + 1;
  }

  // Ids: known (or, for a restore, once handed out), unique, one operation per id.
  const restoreSet = new Set();
  for (const r of restore) {
    if (!isMap(r) || typeof r.id !== "string" || restoreSet.has(r.id)) throw new Error("invalidHoldId");
    if (!byId.has(r.id)) {
      const n = holdSeqOf(r.id);
      if (n === null || n >= seq) throw new Error("invalidHoldId");
    }
    restoreSet.add(r.id);
  }
  const removeSet = new Set();
  for (const id of remove) {
    if (typeof id !== "string" || !byId.has(id) || removeSet.has(id) || restoreSet.has(id)) throw new Error("invalidHoldId");
    removeSet.add(id);
  }
  const polySet = new Set(polyIds);
  const editable = (id) => byId.has(id) && !removeSet.has(id) && !restoreSet.has(id);
  for (const id of polyIds) {
    if (!editable(id)) throw new Error("invalidHoldId");
  }
  for (const id of feetIds) {
    if (!editable(id) || typeof setFeetOnly[id] !== "boolean") throw new Error("invalidHoldId");
  }
  for (const id of noteIds) {
    if (!editable(id)) throw new Error("invalidHoldId");
  }
  for (const id of [...removeSet, ...polyIds, ...feetIds, ...noteIds]) {
    if (byId.get(id).missing === true) throw new Error("holdMissing");
  }
  for (const id of restoreSet) {
    if (byId.has(id) && byId.get(id).missing !== true) throw new Error("holdNotRemoved");
  }

  // Shapes: the returning holds, the new outlines, the added holds.
  const returning = restore.filter((r) => !byId.has(r.id));
  for (const r of returning) {
    if (!RESTORE_SOURCES.has(r.src) || !validateHoldPoly(r.poly)) throw new Error("invalidPoly");
    if (r.feetOnly !== undefined && typeof r.feetOnly !== "boolean") throw new Error("invalidPoly");
  }
  for (const id of polyIds) {
    if (!validateHoldPoly(setPoly[id])) throw new Error("invalidPoly");
  }
  if (!addOk) throw new Error("invalidPoly");
  for (const a of add) {
    if (!isMap(a) || !EDIT_ADD_SOURCES.has(a.src) || !validateHoldPoly(a.poly)) throw new Error("invalidPoly");
    if (a.feetOnly !== undefined && typeof a.feetOnly !== "boolean") throw new Error("invalidPoly");
  }

  if (!noteOk) throw new Error("invalidNote");
  const noteOf = (text) => {
    try {
      return normalizeHoldNote(text);
    } catch {
      throw new Error("invalidNote");
    }
  };
  const notes = new Map(noteIds.map((id) => [id, noteOf(setNote[id])]));
  const returningNotes = returning.map((r) => noteOf(r.note));
  const addNotes = add.map((a) => noteOf(a.note));

  const used = usedIds instanceof Set ? usedIds : new Set(Array.isArray(usedIds) ? usedIds : []);
  const missingIds = [];
  const removedIds = [];
  const edited = [];
  for (const h of base) {
    const id = typeof h.id === "string" ? h.id : null;
    const own = id !== null && byId.get(id) === h;
    if (own && removeSet.has(id)) {
      if (used.has(id)) {
        edited.push({ ...h, missing: true });
        missingIds.push(id);
      } else {
        removedIds.push(id);
      }
      continue;
    }
    const out = { ...h };
    if (own) {
      if (restoreSet.has(id)) delete out.missing;
      if (polySet.has(id)) {
        out.poly = setPoly[id];
        Object.assign(out, holdCentreOf(out.poly));
      }
      if (setFeetOnly[id] === true) out.feetOnly = true;
      else if (setFeetOnly[id] === false) delete out.feetOnly;
      if (notes.has(id)) {
        const note = notes.get(id);
        if (note === null) delete out.note;
        else out.note = note;
      }
    }
    edited.push(out);
  }

  // A returning hold goes back before the first hold with a higher id, so an
  // undone removal leaves the list as it was.
  returning.forEach((r, i) => {
    const hold = { id: r.id, ...holdCentreOf(r.poly), poly: r.poly, src: r.src };
    if (r.feetOnly === true) hold.feetOnly = true;
    if (returningNotes[i] !== null) hold.note = returningNotes[i];
    const n = holdSeqOf(r.id);
    const at = edited.findIndex((h) => {
      const m = holdSeqOf(h.id);
      return m !== null && m > n;
    });
    if (at === -1) edited.push(hold);
    else edited.splice(at, 0, hold);
  });
  if (returning.length > 0 && edited.length > MAX_SPRAY_HOLDS) throw new Error("tooManyHolds");

  if (add.length === 0) return { holds: edited, nextHoldSeq: seq, addedIds: [], missingIds, removedIds };
  const newHolds = add.map((a, i) => {
    const hold = { ...holdCentreOf(a.poly), poly: a.poly, src: a.src };
    if (a.feetOnly === true) hold.feetOnly = true;
    if (addNotes[i] !== null) hold.note = addNotes[i];
    return hold;
  });
  const appended = appendHolds(edited, seq, newHolds);
  const addedIds = appended.holds.slice(edited.length).map((h) => h.id);
  return { holds: appended.holds, nextHoldSeq: appended.nextHoldSeq, addedIds, missingIds, removedIds };
}

// ── Affected-problem list and photo paths ──

/**
 * A copy of an affected-problem row whose name is hidden when the problem is
 * not public (private or friends-level) or hidden, and the caller is neither
 * its setter nor a moderator —
 * the dry-run list must not leak what the problem page itself would not show.
 */
export function maskAffectedName(item, callerUid, callerCanModerate) {
  if (!item || typeof item !== "object") return null;
  const restricted = item.visibility !== "public" || item.status === "hidden";
  const mayRead = callerCanModerate === true || (typeof callerUid === "string" && callerUid !== "" && item.setterUid === callerUid);
  return { ...item, name: restricted && !mayRead ? null : item.name };
}

// ── Detection runs (D48, D68) ──
// One copy for three readers: the `vision` rate limit, the `api` discard
// (`runInProgress`) and, later, the client (a stale run shown as failed).

/** The `detectSprayHolds` `timeoutSeconds`: a `running` run older than this was cut off and never writes back. */
export const DETECT_TIMEOUT_SECONDS = 300;

/** The longest version label (characters): the wall editor field and `startSprayWallVersion` read the same limit. */
export const SPRAY_VERSION_LABEL_MAX = 40;

/** The detection runs one wall may start per hour (D48). */
export const DETECT_RUNS_PER_HOUR = 10;

/** `createdAt` as ms: a Firestore `Timestamp`-like (`toMillis()`), a `Date` or a number; `null` otherwise. */
export function runCreatedMs(createdAt) {
  let ms = null;
  if (typeof createdAt === "number") ms = createdAt;
  else if (createdAt instanceof Date) ms = createdAt.getTime();
  else if (createdAt && typeof createdAt.toMillis === "function") ms = createdAt.toMillis();
  return typeof ms === "number" && Number.isFinite(ms) ? ms : null;
}

/**
 * Is this a `running` detection run whose function may still write its
 * result? Exactly `DETECT_TIMEOUT_SECONDS` old is already stale. An unreadable
 * `createdAt` is not fresh: a run nobody can date must not block forever.
 */
export function isFreshRunningRun(run, nowMs) {
  if (!run || typeof run !== "object" || run.status !== "running") return false;
  const created = runCreatedMs(run.createdAt);
  if (created === null || !Number.isFinite(nowMs)) return false;
  return created > nowMs - DETECT_TIMEOUT_SECONDS * 1000;
}

/**
 * Does this run count against `DETECT_RUNS_PER_HOUR`? `done` and `approved`
 * do (approving must not reset the limit), `dismissed` does too (otherwise
 * run → dismiss → run would get around the limit), a fresh `running` one
 * does; a stale `running`, a `failed` or an unknown one does not. The
 * one-hour window is the caller's query.
 */
export function countsTowardDetectLimit(run, nowMs) {
  if (!run || typeof run !== "object") return false;
  if (run.status === "done" || run.status === "approved" || run.status === "dismissed") return true;
  if (run.status === "running") return isFreshRunningRun(run, nowMs);
  return false;
}

/** How long (hours) a finished, unapproved detection run is offered for review again; `approveSprayHolds` refuses an older one (`runExpired`). */
export const DETECT_RESUME_HOURS = 24;

/**
 * The window (hours) of the wall editor's query for resumable runs: twice the
 * offer, so a run that expired in the last day can still be shown as expired
 * instead of silently vanishing.
 */
export const DETECT_RESUME_QUERY_HOURS = 48;

/**
 * May the wall editor offer this run for review again? Only a `done` run of
 * mode `fresh` or `refresh`, younger than `DETECT_RESUME_HOURS` (strict:
 * exactly 24 hours old is over), whose `baseHoldsRev` still matches the
 * version's `holdsRev` — the very check `approveSprayHolds` makes as
 * `staleRun`, so the editor never offers a review the server would refuse.
 */
export function isResumableRun(run, version, nowMs) {
  if (!run || typeof run !== "object" || !version || typeof version !== "object") return false;
  if (run.status !== "done" || (run.mode !== "fresh" && run.mode !== "refresh")) return false;
  const created = runCreatedMs(run.createdAt);
  if (created === null || !Number.isFinite(nowMs)) return false;
  if (!(created > nowMs - DETECT_RESUME_HOURS * 3600 * 1000)) return false;
  // `?? 0` as in the approval: a version that never had an edit has no `holdsRev`.
  return (run.baseHoldsRev ?? 0) === (version.holdsRev ?? 0);
}

const PENDING_FILE_RE = /^pending_\d{13}\.(?:webp|jpg)$/;
const VERSION_ID_RE = /^[A-Za-z0-9]{20}$/;
const SEGMENT_RE = /^[^/]+$/;

/**
 * Is `path` a Spraywall photo of this wall, under the Storage file-name rule?
 * `prefix: 'pending_'` → `pending_<ms>.(webp|jpg)` (an upload awaiting
 * review); otherwise `prefix` is the 20-character version id and the file is
 * `<versionId>_<ms>.(webp|jpg)`. `<ms>` is a 13-digit `Date.now()`.
 */
export function isSprayPhotoPath(gymId, wallId, path, { prefix } = {}) {
  if (typeof gymId !== "string" || typeof wallId !== "string" || typeof path !== "string") return false;
  if (!SEGMENT_RE.test(gymId) || !SEGMENT_RE.test(wallId)) return false;
  const dir = `gyms/${gymId}/sprayWalls/${wallId}/`;
  if (!path.startsWith(dir)) return false;
  const file = path.slice(dir.length);
  if (prefix === "pending_") return PENDING_FILE_RE.test(file);
  if (typeof prefix !== "string" || !VERSION_ID_RE.test(prefix)) return false;
  return new RegExp(`^${prefix}_\\d{13}\\.(?:webp|jpg)$`).test(file);
}

/** How long an uploaded, never approved photo-swap photo may live. */
export const PENDING_PHOTO_MAX_AGE_MS = 24 * 60 * 60 * 1000;

/**
 * The `Cache-Control` metadata of every Spraywall photo object. Safe to cache
 * for a year without revalidation because a photo path is never reused (the
 * create-only Storage rule and the `isSprayPhotoPath` names): a changed wall
 * is a new file under a new URL. `private`, because draft and pending photos
 * are staff-only.
 */
export const SPRAY_PHOTO_CACHE_CONTROL = "private, max-age=31536000, immutable";

const WALL_FILE_RE = /^gyms\/([^/]+)\/sprayWalls\/([^/]+)\/[^/]+$/;

/**
 * Should this Storage object be deleted as an abandoned photo-swap upload?
 * True only for a `pending_<ms>.(webp|jpg)` file of a wall (the
 * `isSprayPhotoPath` rule) whose `<ms>`, the upload time in the name, is
 * strictly more than 24 hours before `nowMs`. `keepPath` (the run's own
 * file) is never stale; `gymId` / `wallId` narrow the check to one wall.
 * Both sweeps — the per-wall one in `detectSprayHolds` and the daily
 * cross-gym one — use this single rule.
 */
export function isStalePendingPhoto(path, nowMs, { gymId, wallId, keepPath } = {}) {
  if (typeof path !== "string" || !Number.isFinite(nowMs) || path === keepPath) return false;
  const m = WALL_FILE_RE.exec(path);
  if (!m || (gymId !== undefined && m[1] !== gymId) || (wallId !== undefined && m[2] !== wallId)) return false;
  if (!isSprayPhotoPath(m[1], m[2], path, { prefix: "pending_" })) return false;
  const file = path.slice(path.lastIndexOf("/") + 1);
  const ts = Number(file.slice("pending_".length, file.indexOf(".")));
  return nowMs - ts > PENDING_PHOTO_MAX_AGE_MS;
}
